import { message, Input, Tooltip } from "antd";
import { useMutation } from "@apollo/client";
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";

import { validators } from "../constants";
import { useVerificationOTP } from "../hooks";
import { parseError } from "../utils/parseError";
import AuthForm from "../components/forms/AuthForm";
import AuthPageShell from "../components/forms/AuthPageShell";
import BlueButton from "../components/buttons/BlueButton";
import { HeadingOne, BodyOne } from "../components/typography";
import InputValidator from "../components/inputs/InputValidator";
import { CONFIRM_FORGOT_PASSWORD, FORGOT_PASSWORD } from "../graphql/Mutations";
import SupportBlock from "../components/info/SupportBlock";
import mixpanelService from "../services/mixpanel";
import bugfenderHelper from "../services/bugfender";
import ResendOTPBlock from "../components/info/ResendOTPBlock";
import { environments } from "../constants";
import { question_mark_icon } from "../assets/icons";


const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isResetPassword = location.state?.isResetPassword;
  const title = isResetPassword ? "Reset" : "Forgot";

  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const { handleSubmit, formState: { errors }, control } = useForm();
  const { OTP: confirmForgotPasswordOTP, handleSetOTP: handleConfirmForgetPasswordSetOTP } = useVerificationOTP();

  const [forgotPasswordMutation, {
    loading: forgotPasswordLoading,
    data: forgotPasswordData,
    error: forgotPasswordError
  }] = useMutation(FORGOT_PASSWORD);

  const [confirmForgotPasswordMutation, {
    loading: confirmForgotPasswordLoading,
    data: confirmForgotPasswordData,
    error: confirmForgotPasswordError
  }] = useMutation(CONFIRM_FORGOT_PASSWORD);

  const onSubmitForgotPassword = async () => {
    try {
      if (username.length <= 8) {
        message.error("Please enter a valid email/phone number");
        bugfenderHelper.error("Invalid email")
        return;
      }

      await forgotPasswordMutation({
        variables: {
          username: username
        }
      });
    } catch (err) {
      bugfenderHelper.error("Unable to resend code")
      console.error("onSubmitForgotPassword [ERROR]\n", err);
      message.error("We've encountered an error sending the reset code. Please try again later.");
    }
  }

  const onSubmitConfirmForgotPassword = async (data) => {
    try {
      if (confirmForgotPasswordOTP < 6) {
        message.error("Please enter a valid OTP code");
        bugfenderHelper.error("Invalid OTP code entered")
        return;
      };

      if (!data.password) {
        message.error("Please enter a valid password");
        bugfenderHelper.error("Invalid password")
        return;
      };

      await confirmForgotPasswordMutation({
        variables: {
          code: confirmForgotPasswordOTP,
          username,
          password: data.password
        }
      });

    } catch (err) {
      console.error("onSubmitConfirmForgotPassword [ERROR]\n", err);
      message.error("Something went wrong... Please contact our support services");
    }
  };

  useEffect(() => {
    if (!forgotPasswordData) return;

    if (forgotPasswordData?.forgotPassword?.Error) {
      message.error(forgotPasswordData.forgotPassword.Error.message);
      return;
    }

    const processForgotPassword = async () => {
      try {
        if (forgotPasswordData?.forgotPassword) {
          message.success(`Reset code sent to ${username}`);
          bugfenderHelper.log(`Reset code sent to ${username}`)
          setStep(2);
          return;
        }

        throw null;
      } catch (err) {
        bugfenderHelper.error(`Unable to send reset code to ${username} `)
        console.error("processForgotPassword [ERROR]\n", err);
        message.error("We've encountered an error sending the reset code. Please try again later.");
      }
    }
    processForgotPassword();
    mixpanelService.track('App.Forgot.Password.OTP', {
      code: confirmForgotPasswordOTP,
      username: username
    });

  }, [forgotPasswordData]);

  useEffect(() => {
    if (!forgotPasswordError) return;

    const processForgotPasswordError = async () => {
      try {
        const errorMessage = parseError(forgotPasswordError);
        message.error(errorMessage);
      } catch (err) {
        bugfenderHelper.error(err)
        console.error("processForgotPasswordError [ERROR]\n", err);
        message.error("Something went wrong... Please contact our support services");
      }
    }

    processForgotPasswordError();
  }, [forgotPasswordError]);

  useEffect(() => {
    if (!confirmForgotPasswordData) return;

    if (confirmForgotPasswordData?.confirmForgotPassword?.Error) {
      message.error(confirmForgotPasswordData.confirmForgotPassword.Error.message);
      return;
    }

    const processConfirmForgotPassword = async () => {
      try {
        if (confirmForgotPasswordData?.confirmForgotPassword) {
          message.success(confirmForgotPasswordData.confirmForgotPassword?.text || "User Password has Been Reset");
          navigate("/login/mobile-app/MzRlNTgzNGNkMzVka3J2anAwcGpkanAxYmM=");
          return;
        }

        throw null;
      } catch (err) {
        console.error("processConfirmForgotPassword [ERROR]\n", err);
        message.error("We've encountered an error confirming the reset code. Please try again later.");
      }
    }

    processConfirmForgotPassword();
  }, [confirmForgotPasswordData]);

  useEffect(() => {
    if (!confirmForgotPasswordError) return;

    const processConfirmForgotPasswordError = async () => {
      try {
        const errorMessage = parseError(confirmForgotPasswordError);
        message.error(errorMessage);
      } catch (err) {
        bugfenderHelper.error(err)
        console.error("processConfirmForgotPasswordError [ERROR]\n", err);
        message.error("Something went wrong... Please contact our support services");
      }
    }

    processConfirmForgotPasswordError();
  }, [confirmForgotPasswordError]);

  if (step === 2) {
    return (
      <AuthPageShell>
        <form onSubmit={handleSubmit(onSubmitConfirmForgotPassword)}>
          <AuthForm>
            <HeadingOne className="text-[#003eb2] text-center text-2xl font-semibold mb-6">
              Confirm {title} Password
            </HeadingOne>
            <label htmlFor="forgotPasswordOtp" className="block mb-1.5">
              <BodyOne className="mb-0 text-slate-600 font-medium">OTP code</BodyOne>
            </label>
            <Input
              id="forgotPasswordOtp"
              size="large"
              placeholder="Enter the code sent to your email or phone"
              onChange={(e) => handleConfirmForgetPasswordSetOTP(e.target.value)}
            />
            <label htmlFor="forgotPasswordNew" className="block mt-4 mb-1.5">
              <BodyOne className="mb-0 text-slate-600 font-medium" isRequired={true}>New password</BodyOne>
            </label>
            <Controller
              name="password"
              control={control}
              defaultValue=""
              rules={{ required: true, validate: (value) => validators.isPasswordValid(value) }}
              render={({ field: { onChange, value } }) => (
                <InputValidator
                  validatorStates={validators.getPasswordValidatorStates(value)}
                  iconTitles={["8+ Letters", "1 Uppercase", "1 Number"]}
                >
                  <Input.Password
                    id="forgotPasswordNew"
                    style={{ border: "1px solid transparent" }}
                    size="large"
                    placeholder="Enter your new password"
                    onChange={onChange}
                    value={value}
                  />
                </InputValidator>
              )}
            />
            {errors.password && (
              <BodyOne className="text-red-600 mt-1">
                {errors.password.message}
              </BodyOne>
            )}
            <ResendOTPBlock username={username} clientID={environments.REACT_APP_DEPLOY_USER_POOL_WEB_CLIENT_ID} tailwindProps="mt-4" isOTP={false} />
            <BlueButton
              className="w-full mt-6 py-2.5 text-base font-semibold hover:text-white"
              type="submit"
              isLoading={confirmForgotPasswordLoading}
            >
              Confirm {title} Password
            </BlueButton>
            <div className="auth-footer-rule" />
            <SupportBlock area="confirm-forgot-password" tailwindProps="mt-0" />
          </AuthForm>
        </form>
      </AuthPageShell>
    )
  }

  return (
    <AuthPageShell>
      <form onSubmit={handleSubmit(onSubmitForgotPassword)}>
        <AuthForm>
          <HeadingOne className="text-[#003eb2] text-center text-2xl font-semibold mb-6">
            {title} Password
          </HeadingOne>
          <label htmlFor="forgotPasswordUsername" className="flex items-center justify-between mb-1.5">
            <BodyOne className="mb-0 text-slate-600 font-medium">Email or mobile number</BodyOne>
            <Tooltip title={
              <div>
                <p>If you signed up with your email address, please use it to sign in.</p>
                <br />
                <p>Likewise, if you used your cellphone number to create an account, please use it to sign in.</p>
              </div>
            }>
              {question_mark_icon()}
            </Tooltip>
          </label>
          <Input
            id="forgotPasswordUsername"
            size="large"
            placeholder="someone@email.com or +27984895844"
            onChange={(e) => setUsername(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-3 mt-6">
            <BlueButton
              className="bg-white border border-[#0458f6] text-[#0458f6] flex justify-center items-center hover:bg-[#0458f6] hover:text-white"
              type="button"
              style={{ color: "#0458F6" }}
              onClick={() => navigate("/login/mobile-app/MzRlNTgzNGNkMzVka3J2anAwcGpkanAxYmM=")}
            >
              Cancel
            </BlueButton>
            <BlueButton
              className="text-white flex justify-center items-center overflow-hidden py-2.5 font-semibold hover:text-white"
              type="submit"
              isLoading={forgotPasswordLoading}
            >
              <span className="whitespace-nowrap">{title} Password</span>
            </BlueButton>
          </div>
          <div className="auth-footer-rule" />
          <SupportBlock area="forgot-password" tailwindProps="mt-0" />
        </AuthForm>
      </form>
    </AuthPageShell>
  )
}

export default ForgotPassword
